<template>
  <div v-bind="$attrs">
    <el-dialog
      v-model="dialogVisible"
      title=""
      width="1100px"
      :show-close="false"
      :close-on-click-modal="false"
      modal-class="new-order-dialog"
      destroy-on-close
      append-to-body
    >
      <!-- Custom Header -->
      <template #header>
        <div
          class="flex justify-between items-center px-6 py-4 bg-[#004879] text-white rounded-t-[10px]"
        >
          <div class="flex items-center gap-2">
            <img
              src="@/assets/MyPatient/input_order.svg"
              alt="Order"
              class="w-6 h-6 brightness-0 invert"
            />
            <span class="font-semibold text-[20px] leading-normal"
              >{{ configMode ? 'Configure Order' : 'Place Order' }}</span
            >
          </div>
          <button
            class="bg-transparent border-none p-0 cursor-pointer w-7 h-7"
            @click="closeDialog"
          >
            <img
              src="@/assets/MyPatient/close.svg"
              alt="Close"
              class="w-full h-full"
            />
          </button>
        </div>
      </template>

      <!-- Dialog Content -->
      <div class="dialog-content-wrapper relative">
        <!-- Loading overlay covering entire content -->
        <Loading
          v-if="isLoading"
          :loading="isLoading"
          loadingText="Loading..."
        />

        <div class="two-column-layout" :class="{ 'loading': isLoading }">
          <!-- Left Column: Test Selection -->
          <div class="left-column">
            <div class="step-one-container">
              <!-- Step Title -->
              <div class="step-title">
                Step 1: Select Tests
              </div>

              <!-- Category Tabs -->
              <Tab
                v-model="activeProductTab"
                type="outlined"
                :tabs="productTabsForLib"
                class="category-tab"
              />

              <!-- Main Content: Tab-specific content -->
              <div class="order-layout mt-5">
                <div class="products-area">
                  <!-- Vibrant Product Tab Content -->
                  <VibrantProductTabs
                    v-if="activeProductTab === 'vibrant' && orderDataKey > 0"
                    ref="vibrantProductTabsRef"
                    :key="orderDataKey"
                    :highlight-list="highlightsDisplay"
                    @selection-change="handleShortcutSelectionChange"
                  />

                  <!-- Practice Product Tab Content -->
                  <VaMerchPanel v-else-if="activeProductTab === 'practice'" :fixed-col-number="3" />


                  <!-- Program Tab Content -->
                  <ProgramTab
                    v-if="activeProductTab === 'program'"
                    :patient-id="patientId"
                    @selection-change="handleProgramSelectionChange"
                  />

                  <!-- Quick Bill Tab Content -->
                  <div v-else-if="activeProductTab === 'quick_bill'" class="quick-bill-content">
                    <div class="grid grid-cols-3 gap-4 mb-6">
                      <div
                        v-for="p in quickBillProducts"
                        :key="p.id"
                        class="product-card cursor-pointer"
                        :class="{ 'selected': cart.some(item => item.id === p.id) }"
                        @click="addToCart(p)"
                      >
                        <div class="flex items-start gap-3">
                          <img style="width: 40px; height: 40px;" :src="productImageSrc(p)" alt="Product" />
                          <div class="flex-1">
                            <div class="line-clamp-2">{{ p.name }}</div>
                            <div class="text-[12px]">{{ p.subtests.length }} Subtests</div>
                          </div>
                        </div>
                        <div class="mt-4 pt-3 border-t border-dashed border-[#E5E7EB] flex items-center justify-end">
                          <div class="text-[12px] text-[#666]">Unit Price: <span>${{ p.price.toFixed(2) }}</span></div>
                        </div>
                      </div>
                    </div>
                    <div class="flex flex-col gap-3 ml-4 mt-8 border-t border-dashed border-[#E5E7EB]">
                      <span class="font-medium mt-4">Add New Quick Bill:</span>
                      <div class="flex flex-row items-center gap-5">
                        <span class="text-end w-[133px] font-medium">Charge For</span>
                        <BaseInput2 placeholder="Charge For" class="w-[400px]" />
                      </div>
                      <div class="flex flex-row items-center gap-5">
                        <span class="text-end w-[133px] font-medium">Amount</span>
                        <BaseInput2 placeholder="Amount" class="w-[400px]" />
                      </div>
                      <DynamicColorResponsiveButton
                        class="flex mx-auto mr-0"
                        button_type="outlined"
                        built_in_theme="primary"
                        button_border_radius="4px"
                        display_name="Add to Cart"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Right Column -->
          <div
            v-if="isVibrantFlowTab"
            :class="[
              'right-column',
              'order-summary-sidebar',
              { 'order-summary-expanded': isOrderSummaryExpanded },
            ]"
          >
            <div class="order-summary-panel">
              <VaOrderSummary
                ref="vaOrderSummaryRef"
                class="order-summary-embedded"
                :order-draft="{}"
                :show-accessories="false"
                :show-item-type-group="true"
                :expanded="isOrderSummaryExpanded"
                @toggle-expand="toggleOrderSummaryExpand"
              />
              <div class="checkout-btn-wrapper order-summary-checkout-btn-wrapper">
                <DynamicColorResponsiveButton
                  v-if="configMode"
                  button_type="filled"
                  built_in_theme="primary"
                  button_border_radius="4px"
                  display_name="Continue"
                  :disabled="currentCheckoutCart.length === 0"
                  @click-button="goToCheckout"
                />
                <VaCheckoutButtons
                  v-else
                  :total-price="selectedItemsPrice"
                  :payment-method="paymentMethod"
                  :is-disable-checkout="currentCheckoutCart.length === 0"
                  :show-secondary-actions="false"
                  @handleCheckout="goToCheckout"
                />
              </div>
            </div>
          </div>
          <div
            v-else
            :class="[
              'right-column',
              'cart-sidebar',
              { 'cart-expanded': isCartExpanded },
            ]"
          >
            <new-order-cart-items
              :collapsed="false"
              :items="cart"
              :hovered="cartHovered"
              :expanded="isCartExpanded"
              @toggle-expand="toggleCartExpand"
              @update:items="updateCart"
              @remove-item="handleCartItemRemove"
            />
            <div class="checkout-btn-wrapper">
              <DynamicColorResponsiveButton
                button_type="filled"
                built_in_theme="primary"
                button_border_radius="4px"
                :display_name="configMode ? 'Continue' : 'Checkout'"
                :disabled="cart.length === 0"
                @click-button="goToCheckout"
              />
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import NewOrderCartItems from './NewOrderCartItems.vue';
import BaseInput2 from '@/NewLibrary/BaseInput2.vue';
import VibrantProductTabs from './VibrantProductTabs.vue';
import ProgramTab from './ProgramTab.vue';
import VaOrderSummary from '@/views/va-orderTest/order-page/order-summary/index.vue';
import VaMerchPanel from '@/views/va-orderTest/order-page/order-test-panel/order-test-tabs/merch-panel/index.vue';
import VaCheckoutButtons from '@/views/va-orderTest/order-page/order-summary/SummaryAccessories/Buttons.vue';
import { getTestsOrBundles } from '@/views/va-orderTest/order-page/order-summary/selected-tests-tab-pane/index.js';
import { mapState, mapGetters } from 'vuex';
import { getHighlightCardsApi } from '@/api/va-order-page/pricing/item/price';
import { parseJson } from '@/utils';
import { notify } from '@/utils/notify';
import Loading from '@/NewLibrary/Loading/Loading.vue';
import { handlePatientPurchaseEligibility } from '@/views/va-orderTest/order-page/patientPurchaseEligibility.js';

export default {
  name: "PlaceOrderDialog",
  components: {
    NewOrderCartItems,
    BaseInput2,
    VibrantProductTabs,
    ProgramTab,
    VaOrderSummary,
    VaMerchPanel,
    VaCheckoutButtons,
    Loading,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    roomId: {
      type: String,
      default: "",
    },
    selectedProviderId: {
      type: String,
      default: "",
    },
    patientId: {
      type: [String, Number],
      default: "",
    },
    configMode: {
      type: Boolean,
      default: false,
    },
    initialItems: {
      type: Array,
      default: () => [],
    },
    initialIsDbsVersion: {
      type: Boolean,
      default: null,
    },
    initialOrderItems: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'checkout', 'save'],
  data() {
    return {
      orderDataKey: 0,
      isLoading: false,
      hasLoadedOrderData: false,
      lastLoadedProviderId: "",
      allProductTabs: [
        { id: "vibrant", label: "Vibrant Product" },
        { id: "practice", label: "Practice Product" },
        { id: "program", label: "Program" },
        // { id: "quick_bill", label: "Quick Bill" },
      ],
      activeProductTab: "vibrant",
      products: [
        {
          id: "p1",
          tab: "vibrant",
          name: "Gut Zoomer",
          subtests: ["Gut Commensals", "Gut Pathogens", "Gut Inflammation & Metabolites"],
          price: 500,
          isHovered: false,
        },
        {
          id: "p2",
          tab: "vibrant",
          name: "Insulin Panel",
          subtests: ["Adiponectin", "Ferritin"],
          price: 11.91,
          isHovered: false,
        },
        {
          id: "p3",
          tab: "vibrant",
          name: "Oxidative Stress",
          subtests: ["Antioxidant Genetics Panel", "Oxidative Damage Markers"],
          price: 520,
          isHovered: false,
        },
        {
          id: "p4",
          tab: "vibrant",
          name: "Total Tox Burden",
          subtests: ["Heavy Metals - Urine", "Environmental Toxins", "Mycotoxins"],
          price: 520,
          isHovered: false,
        },
        {
          id: "p5",
          tab: "vibrant",
          name: "Replenish",
          subtests: ["Replenish - Regenere Eye Serum - 20ml", "Replenish - Regenere Face Serum - 30ml"],
          price: 400,
          isHovered: false,
        },
        {
          id: "p6",
          tab: "vibrant",
          name: "Wheat Zoomer",
          subtests: ["Wheat Zoomer", "Intestinal Permeability Panel"],
          price: 380,
          isHovered: false,
        },
        {
          id: "p7",
          tab: "practice",
          name: "Practice Alpha",
          subtests: ["Practice Alpha", "Practice Beta", "Practice Gamma"],
          price: 200,
          isHovered: false,
        },
        {
          id: "p8",
          tab: "bundle",
          name: "Anti-Aging",
          subtests: ["Albumin", "Hormones (all)", "Micronutrients"],
          price: 520,
          isHovered: false,
        },
        {
          id: "p9",
          tab: "quick_bill",
          name: "Custom Quick Bill",
          subtests: ["Custom Quick Bill alpha", "Custom Quick Bill beta"],
          price: 80,
          isHovered: false,
        },
      ],
      cart: [],
      isSubscription: false,
      cartHovered: false,
      isCartExpanded: false,
      isOrderSummaryExpanded: false,
    };
  },

  computed: {
    ...mapState("orderTest", ["isDbsVersion"]),
    ...mapState("orderTest/order", ["paymentMethod"]),
    ...mapState("orderTest/promotions", ["vibrantHighlightList"]),
    ...mapGetters("orderTest/tests", ["bfsTestList"]),
    ...mapGetters("orderTest/panels", ["bundleList"]),
    ...mapGetters("orderTest", ["selectedItemList", "selectedItemsPrice"]),
    ...mapGetters("orderTest/merchandise", ["selectedMerchList"]),
    productTabs() {
      if (this.configMode) {
        return this.allProductTabs.filter(tab => tab.id !== 'program');
      }
      return this.allProductTabs;
    },
    productTabsForLib() {
      return this.productTabs.map(tab => ({ value: tab.id, label: tab.label, icon: false }));
    },
    dialogVisible: {
      get() {
        return this.visible;
      },
      set() {
        // Do nothing here - dialog close is handled by closeDialog method
        // This prevents auto-close when switching between dialogs
      },
    },
    practiceProducts() {
      return this.products.filter((p) => p.tab === 'practice');
    },
    bundleProducts() {
      return this.products.filter((p) => p.tab === 'bundle');
    },
    quickBillProducts() {
      return this.products.filter((p) => p.tab === 'quick_bill');
    },
    subtotal() {
      return this.cart.reduce((sum, i) => sum + i.price, 0);
    },
    highlightsDisplay() {
      const highlightDbsVersion = this.isDbsVersion ? "at_home" : "not_at_home";
      return (this.vibrantHighlightList || []).filter(
        (highlight) => highlight.section === highlightDbsVersion || highlight.section === "both"
      );
    },
    isVibrantFlowTab() {
      return ["vibrant", "practice", "program"].includes(this.activeProductTab);
    },
    vibrantCheckoutCart() {
      return (this.selectedItemList || []).map((item) => ({
        id: item.id,
        bundleId: item.bundleId,
        category: item.category,
        tab: "vibrant",
        name: item.displayName || item.testName || item.name,
        subtests: [],
        price: this.resolveCheckoutItemUnitPrice(item),
        isHovered: false,
        originalItem: item,
      }));
    },
    practiceCheckoutCart() {
      return (this.selectedMerchList || []).map((item) => ({
        id: item.id,
        category: "practice",
        tab: "practice",
        name: item?.merch?.name || "",
        subtests: [],
        quantity: item.quantity,
        price: item?.merch?.currentPrice || 0,
        originalItem: item,
      }));
    },
    currentCheckoutCart() {
      return this.isVibrantFlowTab
        ? this.vibrantCheckoutCart.concat(this.practiceCheckoutCart)
        : this.cart;
    },
  },
  watch: {
    visible: {
      immediate: true,
      async handler(newVal) {
        if (newVal) {
          const currentProviderId = String(this.selectedProviderId || "");
          const shouldFetchOrderData = !this.hasLoadedOrderData || this.lastLoadedProviderId !== currentProviderId;
          if (shouldFetchOrderData) {
            await this.fetchOrderData();
          }
          // Note: removed else branch that only fetched shortcuts
          // Shortcuts are always fetched as part of fetchOrderData via orderPageFetchData
        }
      }
    },
    currentCheckoutCart(newVal) {
      // In configMode, toggle consultation fee usage based on whether cart has items.
      // initConsultationFee sets isUse=true immediately (payment is patientPayLater),
      // but we reset it to false so subtotal starts at $0. This watcher re-enables it
      // once the user adds items.
      if (this.configMode) {
        const hasItems = newVal && newVal.length > 0;
        const cf = this.$store.state.orderTest.order.consultationFee;
        if (cf.amount > 0 && cf.isUse !== hasItems) {
          this.$store.commit('orderTest/order/SET_CONSULTATION_FEE_USAGE', hasItems);
        }
      }
    },
    async selectedProviderId(newVal, oldVal) {
      if (String(newVal || "") !== String(oldVal || "")) {
        console.log('[PlaceOrderDialog] selectedProviderId changed from', oldVal, 'to', newVal);
        this.hasLoadedOrderData = false;
        this.lastLoadedProviderId = "";
        // If dialog is open, immediately fetch new data
        if (this.visible) {
          console.log('[PlaceOrderDialog] Dialog is open, fetching new data for provider:', newVal);
          await this.fetchOrderData();
        }
      }
    }
  },
  mounted() {
    this.preloadTabImages();
  },
  methods: {
    async fetchOrderData() {
      console.log('[PlaceOrderDialog] fetchOrderData START, selectedProviderId:', this.selectedProviderId);
      this.isLoading = true;
      try {
        // First, initialize the store to clear any stale data
        console.log('[PlaceOrderDialog] calling initializeStore...');
        await this.$store.dispatch('orderTest/initializeStore');
        console.log('[PlaceOrderDialog] initializeStore DONE');
        
        // Restore patientCountry from sessionStorage (initializeStore clears it)
        const sessionPatient = JSON.parse(sessionStorage.getItem('patient') || '{}');
        if (sessionPatient.country) {
          this.$store.dispatch('orderTest/user/setPatientCountry', sessionPatient.country);
        }

        // Load country options for international shipping fee calculation
        this.$store.dispatch('orderTest/setCountryOptions');

        // Ensure orderTest userInfo is loaded via API (same as order page)
        console.log('[PlaceOrderDialog] calling setUserInfo...');
        await this.$store.dispatch('orderTest/user/setUserInfo');
        
        // For clinic users, need to set customer list and default customer first
        // This is the same flow as order-page/index.vue mounted()
        const userInfo = this.$store.state.orderTest.user.userInfo;
        if (userInfo && userInfo.isClinic) {
          await this.$store.dispatch('orderTest/user/setCustomerList');
          if (this.selectedProviderId) {
            const selectedCustomer = this.$store.state.orderTest.user.customerList.find(
              (customer) => String(customer.id) === String(this.selectedProviderId)
            );
            if (selectedCustomer) {
              this.$store.dispatch('orderTest/user/setCustomer', selectedCustomer);
            }
          } else {
            await this.$store.dispatch('orderTest/user/setDefaultCustomer');
          }
        }
        
        // Now fetch all order page data (tests, bundles, promotions, etc.)
        await this.$store.dispatch('orderTest/orderPageFetchData');

        // Handle patient purchase eligibility and VA panel visibility
        // (same as order-page/index.vue mounted — must be called after orderPageFetchData)
        // Re-read userInfo from store to pick up any updates made inside orderPageFetchData
        await handlePatientPurchaseEligibility(this.bfsTestList, this.$store.state.orderTest.user.userInfo);

        // After all test/bundle data is loaded, now load shortcuts
        // This ensures shortcuts can calculate prices with loaded data
        console.log('[PlaceOrderDialog] orderPageFetchData DONE, now loading shortcuts...');
        this.$store.commit('orderTest/favorites/SET_SHORTCUT_LIST', []);
        await this.$store.dispatch('orderTest/favorites/setShortcutList');
        console.log('[PlaceOrderDialog] shortcuts loaded');

        // Load customer special promotions (aligned with VA Portal checkout)
        this.$store.dispatch('orderTest/order/setCustomerSpecialPromos');

        // In configMode, set payment method so practice products are enabled
        // and clear fees so subtotal starts at $0 for new orders
        if (this.configMode) {
          this.$store.commit('orderTest/order/SET_PAYMENT_METHOD', 'patientPayLater');
          this.$store.commit('orderTest/order/SET_LAB_PROCESSING_FEE', 0);
          this.$store.commit('orderTest/order/RESET_SHIPPING_FEE');

          // Reset consultation fee: keep the amount/name from defaultSettings
          // but set isUse to false so it doesn't inflate subtotal when cart is empty.
          // InternationalShipping.initConsultationFee sets isUse=true because payment
          // is already patientPayLater, but for configMode the fee should only count
          // once the user actually selects items.
          const { amount: cfAmt, name: cfName } = this.$store.state.orderTest.order.consultationFee;
          if (cfAmt > 0) {
            this.$store.commit('orderTest/order/SET_CONSULTATION_FEE', { amount: cfAmt, isUse: false, name: cfName });
          }
        }

        this.hasLoadedOrderData = true;
        this.lastLoadedProviderId = String(this.selectedProviderId || "");
        this.orderDataKey++; // Render VP tabs once core order data is ready

        if (this.initialOrderItems.length > 0) {
          this.restoreFromOrderItems();
          if (this.configMode) {
            const cf = this.$store.state.orderTest.order.consultationFee;
            if (cf.amount > 0) {
              this.$store.commit('orderTest/order/SET_CONSULTATION_FEE_USAGE', true);
            }
          }
        } else if (this.initialItems.length > 0) {
          this.restoreInitialItems();
          if (this.configMode) {
            const cf = this.$store.state.orderTest.order.consultationFee;
            if (cf.amount > 0) {
              this.$store.commit('orderTest/order/SET_CONSULTATION_FEE_USAGE', true);
            }
          }
        }

        const currency = parseJson(localStorage.getItem("currency"));
        const currencyName = currency && currency.name ? currency.name.toLowerCase() : "usd";
        try {
          const { data: highlights } = await getHighlightCardsApi(currencyName);
          (highlights || []).forEach((highlight, index) => {
            highlight.id = index;
          });
          this.$store.dispatch("orderTest/promotions/setVibrantHighlightList", highlights || []);
        } catch (highlightError) {
          console.error('Failed to fetch highlights data:', highlightError);
          this.$store.dispatch("orderTest/promotions/setVibrantHighlightList", []);
        }
      } catch (error) {
        console.error('Failed to fetch order data:', error);
        this.hasLoadedOrderData = false;
      } finally {
        this.isLoading = false;
      }
    },
    preloadTabImages() {
      const tabImages = [
        'vibrant-product-hover.svg',
        'vibrant-product-selected.svg',
        'vibrant-product-default.svg',
        'practice-product-hover.svg',
        'practice-product-selected.svg',
        'practice-product-default.svg',
        'bundle-hover.svg',
        'bundle-selected.svg',
        'bundle-default.svg',
        'quick-bill-hover.svg',
        'quick-bill-selected.svg',
        'quick-bill-default.svg',
      ];
      tabImages.forEach((img) => {
        const image = new Image();
        image.src = require(`@/assets/newOrder/${img}`);
      });
    },
    isInCart(productId) {
      return this.cart.some((i) => i.id === productId);
    },
    closeDialog() {
      this.resetState();
      this.$emit("close");
    },
    resetState() {
      this.cart = [];
      this.cartHovered = false;
      this.isCartExpanded = false;
      this.isOrderSummaryExpanded = false;
      this.activeProductTab = "vibrant";
      this.isSubscription = false;
    },
    addToCart(product) {
      if (!this.isInCart(product.id)) {
        this.cart.push({ ...product });
      }
    },
    updateCart(newCart) {
      this.cart = newCart;
    },
    toggleCartExpand() {
      this.isCartExpanded = !this.isCartExpanded;
      this.cartHovered = this.isCartExpanded;
    },
    toggleOrderSummaryExpand() {
      this.isOrderSummaryExpanded = !this.isOrderSummaryExpanded;
    },
    productImageSrc(p) {
      const isInCart = this.isInCart(p.id);
      const state = p.isHovered && !isInCart
        ? 'hover'
        : isInCart
          ? 'selected'
          : 'default';

      const base = {
        vibrant: 'vibrant-product',
        practice: 'practice-product',
        bundle: 'bundle',
        quick_bill: 'quick-bill',
      };

      const tabKey = base[this.activeProductTab] || base.quick_bill;
      return require(`@/assets/newOrder/${tabKey}-${state}.svg`);
    },
    resolveCheckoutItemUnitPrice(item) {
      if (!item) {
        return 0;
      }
      if (item.category !== "shortcut") {
        return Number(item.price) || 0;
      }
      const includedItems = getTestsOrBundles(item, this.bfsTestList || [], this.bundleList || []);
      return includedItems.reduce((sum, includedItem) => sum + (Number(includedItem?.price) || 0), 0);
    },
    shouldDeferPayLaterNotificationValidation(summaryRef) {
      return (
        summaryRef?.paymentMethod === 'patientPayLater'
        && !summaryRef?.sendNotification?.email
        && !summaryRef?.sendNotification?.text
      );
    },
    goToCheckout() {
      if (this.currentCheckoutCart.length === 0) return;
      // Skip validation in configMode
      if (this.configMode) {
        this.$emit("checkout", {
          cart: [...this.currentCheckoutCart],
          isSubscription: this.isSubscription,
          isDbsVersion: this.isDbsVersion,
        });
        return;
      }
      // For Vibrant flow, run validation from VaOrderSummary but don't navigate
      if (this.isVibrantFlowTab && this.$refs.vaOrderSummaryRef) {
        const summaryRef = this.$refs.vaOrderSummaryRef;
        const shouldDeferPayLaterNotification = this.shouldDeferPayLaterNotificationValidation(summaryRef);
        const blockReason = typeof summaryRef.getCheckoutBlockReasonForEhr === 'function'
          ? summaryRef.getCheckoutBlockReasonForEhr()
          : '';

        if (blockReason && !shouldDeferPayLaterNotification) {
          notify({
            type: 'error',
            title: 'Unable to checkout',
            message: blockReason,
            duration: 5000,
            showClose: false,
          });
          return;
        }
      }
      this.$emit("checkout", {
        cart: [...this.currentCheckoutCart],
        isSubscription: this.isSubscription,
      });
    },
    handleCartItemRemove(item) {
      if (!item) {
        return;
      }
      if (item.tab === 'vibrant' && this.$refs.vibrantProductTabsRef?.removeSelectedItemFromCart) {
        this.$refs.vibrantProductTabsRef.removeSelectedItemFromCart(item.originalItem || item);
      }
    },
    handleShortcutSelectionChange(selectedList, totalPrice) {
      // Shortcut selections are handled via Vuex store
      // The selectedItemList getter will include shortcut items
      // This method is a placeholder for any additional handling needed
    },
    handleProgramSelectionChange(cartItems, totalPrice) {
      // Program selection is now handled via Vuex store
      // This method is kept for backward compatibility
    },
    restoreFromOrderItems() {
      const orderItems = this.initialOrderItems;
      console.log('[PlaceOrderDialog] restoreFromOrderItems - VA-format items:', orderItems);

      // Restore DBS version (Home Collection toggle)
      if (this.initialIsDbsVersion !== null) {
        this.$store.dispatch('orderTest/setIsDbsVersion', this.initialIsDbsVersion);
      }

      // Set flag to prevent findBestDeal from modifying restored items
      this.$store.commit('orderTest/SET_IS_RESTORING_ITEMS', true);

      const testList = this.$store.getters['orderTest/tests/testList'] || [];
      const bundleList = this.$store.getters['orderTest/panels/bundleList'] || [];
      const shortcutList = this.$store.state.orderTest.favorites.shortcutList || [];

      // 1. Process shortcuts first (shortcutId items)
      orderItems
        .filter(item => item.item_type === 'shortcutId')
        .forEach(item => {
          const shortcutOnMenu = shortcutList.find(s => s.id === item.item_id);
          if (shortcutOnMenu) {
            this.$store.dispatch('orderTest/addSelectedShortcut', { id: item.item_id });
          } else {
            console.warn('[PlaceOrderDialog] shortcut not on menu:', item.item_id);
          }
        });

      // 2. Process individual tests (packagePriceId) - skip items belonging to a shortcut or promotion
      orderItems
        .filter(item => item.item_type === 'packagePriceId' && !item.shortcut_id && !item.promotion_coupon_id)
        .forEach(item => {
          let testId = item.package_id ? [item.item_id, item.package_id].join('_') : item.item_id;
          const foundTest = testList.find(t => t.id === testId);
          if (foundTest) {
            this.$store.dispatch('orderTest/addSelectedTest', foundTest);
          } else if (!isNaN(testId)) {
            // Try to find by originalId for duplicate tests
            const byOriginal = testList.find(t => t.originalId === testId);
            if (byOriginal) {
              this.$store.dispatch('orderTest/addSelectedTest', byOriginal);
            } else {
              console.warn('[PlaceOrderDialog] test not on menu:', testId);
            }
          }
        });

      // 3. Process bundles (bundleId) - skip items belonging to a shortcut
      orderItems
        .filter(item => item.item_type === 'bundleId' && !item.shortcut_id)
        .forEach(item => {
          const bundle = bundleList.find(b => b.bundleId === item.item_id);
          if (!bundle) {
            console.warn('[PlaceOrderDialog] bundle not on menu:', item.item_id);
            return;
          }

          if (bundle.category === 'discountPanel') {
            this.$store.dispatch('orderTest/addSelectedDiscountPanel', bundle);
          } else if (bundle.category === 'xChooseY' || item.x_choose_y_List) {
            // xChooseY panel: restore selected zoomer and supplement IDs
            if (item.x_choose_y_List && item.x_choose_y_List[0]) {
              const xChooseYDetails = item.x_choose_y_List[0].x_choose_y_detail_list || [];
              bundle.selectedZoomerIdList = xChooseYDetails.map(d => d.item_id);
            }
            if (item.supplement_x_choose_y_List && item.supplement_x_choose_y_List[0]) {
              const supplementDetails = item.supplement_x_choose_y_List[0].x_choose_y_detail_list || [];
              bundle.selectedSupplementIdList = supplementDetails.map(d => d.item_id);
            }
            this.$store.dispatch('orderTest/addSelectedMultiChoosePanel', { bundleId: item.item_id });
          } else {
            // Custom panel - may contain xChooseY sub-panels
            if (item.x_choose_y_List && item.x_choose_y_List.length > 0) {
              const officialPanelIds = (bundle.bundleItemList || [])
                .filter(bi => bi.itemType === 'official bundle')
                .map(bi => bi.itemId);
              const xChooseYPanels = bundleList.filter(b =>
                officialPanelIds.includes(b.bundleId) && b.quantity
              );
              xChooseYPanels.forEach((xPanel, i) => {
                if (item.x_choose_y_List[i]) {
                  xPanel.selectedZoomerIdList = (item.x_choose_y_List[i].x_choose_y_detail_list || []).map(d => d.item_id);
                }
                if (item.supplement_x_choose_y_List && item.supplement_x_choose_y_List[i]) {
                  xPanel.selectedSupplementIdList = (item.supplement_x_choose_y_List[i].x_choose_y_detail_list || []).map(d => d.item_id);
                }
                xPanel.isComplete = true;
                if (!bundle.selectedXChooseYPanelList) {
                  bundle.selectedXChooseYPanelList = [];
                }
                bundle.selectedXChooseYPanelList.push(xPanel);
              });
            }
            this.$store.dispatch('orderTest/addSelectedCustomPanel', bundle);
          }
        });

      // 4. Process merchandise
      orderItems
        .filter(item => item.item_type === 'merchandise')
        .forEach(item => {
          const merchId = item.item_type_id || item.item_id;
          const quantity = item.quantity || 1;
          const merchMap = this.$store.getters['orderTest/merchandise/merchMap'];
          const merch = merchMap ? merchMap[merchId] : null;
          if (merch) {
            this.$store.dispatch('orderTest/merchandise/addToSelectedMerchMapWithQuantity', { id: merchId, merch, quantity });
          } else {
            console.warn('[PlaceOrderDialog] merch not found for id:', merchId);
          }
        });

      // 5. Mark draft loading as complete (same as VA)
      this.$store.state.orderTest.orderDraft.loadDraft.customPanel = true;
      this.$store.state.orderTest.orderDraft.loadDraft.orderTestPanel = true;

      // Clear flag after next tick
      this.$nextTick(() => {
        this.$store.commit('orderTest/SET_IS_RESTORING_ITEMS', false);
      });
    },
    restoreInitialItems() {
      console.log('[PlaceOrderDialog] restoring initial items (legacy):', this.initialItems);
      if (this.initialIsDbsVersion !== null) {
        this.$store.dispatch('orderTest/setIsDbsVersion', this.initialIsDbsVersion);
      }
      this.$store.commit('orderTest/SET_IS_RESTORING_ITEMS', true);
      for (const item of this.initialItems) {
        const category = item.category;
        try {
          if (category === 'shortcut') {
            this.$store.dispatch('orderTest/addSelectedShortcut', { id: item.id });
          } else if (category === 'discountPanel') {
            this.$store.dispatch('orderTest/addSelectedDiscountPanel', { bundleId: item.bundleId || item.id });
          } else if (category === 'customPanel') {
            this.$store.dispatch('orderTest/addSelectedCustomPanel', { bundleId: item.bundleId || item.id });
          } else if (category === 'xChooseY') {
            if (item.selectedZoomerIdList || item.selectedSupplementIdList) {
              const bundleList = this.$store.getters['orderTest/panels/bundleList'];
              const panel = bundleList.find(b => b.bundleId === (item.bundleId || item.id));
              if (panel) {
                panel.selectedZoomerIdList = item.selectedZoomerIdList || [];
                panel.selectedSupplementIdList = item.selectedSupplementIdList || [];
              }
            }
            this.$store.dispatch('orderTest/addSelectedMultiChoosePanel', { bundleId: item.bundleId || item.id });
          } else if (category === 'practice') {
            const quantity = item.count || 1;
            const merchMap = this.$store.getters['orderTest/merchandise/merchMap'];
            const merch = merchMap ? merchMap[item.id] : null;
            if (merch) {
              this.$store.dispatch('orderTest/merchandise/addToSelectedMerchMapWithQuantity', { id: item.id, merch, quantity });
            }
          } else {
            this.$store.dispatch('orderTest/addSelectedTest', { id: item.id });
          }
        } catch (err) {
          console.warn('[PlaceOrderDialog] Failed to restore item:', item, err);
        }
      }
      this.$nextTick(() => {
        this.$store.commit('orderTest/SET_IS_RESTORING_ITEMS', false);
      });
    },
    saveConfiguration() {
      if (this.currentCheckoutCart.length === 0) return;
      const items = this.currentCheckoutCart.map(item => ({
        id: item.id,
        bundleId: item.bundleId || null,
        category: item.category || item.tab,
        name: item.name,
        count: item.quantity || 1,
        price: Number(item.price).toFixed(2),
        subtests: item.subtests || [],
      }));
      console.log('[PlaceOrderDialog] saveConfiguration items:', JSON.parse(JSON.stringify(items)));
      this.$emit("save", items);
      this.resetState();
      this.$emit("close");
    },
  },
};
</script>

<style>
.new-order-dialog .el-overlay-dialog {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.new-order-dialog .el-dialog {
  border-radius: 10px;
  margin: 0 auto;
  --el-dialog-padding-primary: 0px !important;
  max-height: calc(100vh - 40px) !important;
  height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Inter', sans-serif;
}

.new-order-dialog .el-dialog__header {
  padding: 0;
  border-bottom: none;
  flex-shrink: 0;
}

.new-order-dialog .el-dialog__body {
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  min-height: 0;
}

.new-order-dialog .el-dialog__footer {
  display: none;
}
</style>

<style scoped>
.product-card:hover {
  border-color: #8BBDC7;
}

.product-card.selected {
  border-color: #004879;
}

/* Two Column Layout */
.dialog-content-wrapper {
  padding: 24px;
  background: #f9fbfd;
  color: #333;
  min-height: 500px;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  font-family: 'Inter', sans-serif;
  border-radius: 0 0 10px 10px;
  position: relative;
}

.two-column-layout {
  display: flex;
  flex-direction: row;
  position: relative;
  flex: 1;
  height: 100%;
  min-height: 0;
  gap: 16px;
}

.two-column-layout.loading {
  opacity: 0.3;
  pointer-events: none;
}

.left-column {
  flex: 1;
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.right-column {
  width: 286px;
  min-width: 286px;
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
}

.cart-sidebar {
  width: 286px;
  height: 100%;
  min-height: 0;
  padding: 16px 16px 0;
  border-radius: 8px;
  background: #FFF;
  box-shadow: -3px -1px 6px 0 rgba(74, 74, 74, 0.14);
  transition: width 220ms ease-in-out, box-shadow 220ms ease-in-out;
  overflow: hidden;
  gap: 8px;
}

.order-summary-sidebar {
  width: 286px;
  min-width: 286px;
  height: 100%;
  min-height: 0;
  padding: 0;
  overflow: visible;
  position: relative;
}

.order-summary-sidebar.order-summary-expanded {
  z-index: 60;
}

.order-summary-panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 286px;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background: #FFF;
  box-shadow: -3px -1px 6px 0 rgba(74, 74, 74, 0.14);
  overflow: hidden;
  transition: width 220ms ease-in-out, box-shadow 220ms ease-in-out;
}

.order-summary-sidebar.order-summary-expanded .order-summary-panel {
  width: 420px;
  
}

.order-summary-embedded {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.order-summary-checkout-btn-wrapper {
  flex-shrink: 0;
  /* border-top: 1px dashed #C9CACD; */
  height: 64px;
  padding: 8px 12px 12px;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  background: #fff;
}

.order-summary-sidebar :deep(.order-summary-title) {
  padding-left: 36px;
}

.order-summary-sidebar :deep(.order-summary-container) {
  height: 100%;
  overflow-y: auto;
  padding: 16px 16px 24px !important;
}

.cart-sidebar.cart-expanded {
  width: 560px;
  box-shadow: -6px 0 16px rgba(74, 74, 74, 0.2);
  z-index: 60;
}

.cart-sidebar :deep(.cart-container) {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.checkout-btn-wrapper {
  height: 64px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  padding: 12px 0 12px 0;
  margin-top: auto;
  background: #f9fbfd;
}

.checkout-btn-wrapper :deep(button) {
  min-width: 113px;
  height: 40px;
  padding: 8px 24px;
  border-radius: 4px;
  box-shadow: 0 1px 3px 0 rgba(74, 74, 74, 0.25);
}

.order-summary-checkout-btn-wrapper :deep(.bottom-fixed-container) {
  position: static !important;
  width: 100%;
  max-width: 100%;
  margin: 0;
  display: flex;
  justify-content: flex-end;
}

.order-summary-checkout-btn-wrapper :deep(.checkout-btn),
.order-summary-checkout-btn-wrapper :deep(.checkout-btn-disable) {
  width: 160px !important;
  min-width: 160px !important;
  max-width: 160px !important;
}

.step-one-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  border-radius: 5px;
  padding: 0;
  width: 100%;
}

.step-title {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #333;
  padding-bottom: 10px;
  border-bottom: 1px dashed #c4c4c4;
  margin-bottom: 12px;
}

.category-tab {
  margin-top: 8px;
}

.order-layout {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  min-height: 0;
  gap: 16px;
}

.products-area {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

/* Thin scrollbar for Practice Product (VaMerchPanel) */
.products-area :deep(.merch-content > :first-child) {
  scrollbar-width: thin;
  scrollbar-color: #c8d2de transparent;
}

.products-area :deep(.merch-content > :first-child::-webkit-scrollbar) {
  width: 6px;
  height: 6px;
  background: transparent;
}

.products-area :deep(.merch-content > :first-child::-webkit-scrollbar-track) {
  background: transparent;
}

.products-area :deep(.merch-content > :first-child::-webkit-scrollbar-thumb) {
  background: #c8d2de;
  border-radius: 999px;
}

.products-area :deep(.merch-content > :first-child::-webkit-scrollbar-thumb:hover) {
  background: #aebcca;
}

.products-area :deep(.vibrant-product-tabs) {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  min-height: 0;
}

.products-area :deep(.vibrant-product-tabs .tab-content) {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.products-area :deep(.vibrant-product-tabs .order-test-panel-content) {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.products-area :deep(.vibrant-product-tabs .order-test-panel-wrapper) {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0 !important;
  height: 100%;
  max-height: 100% !important;
  overflow: hidden;
}

.products-area :deep(.vibrant-product-tabs .order-test-panel-content > div) {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
}

.products-area :deep(.vibrant-product-tabs .order-test-panel-wrapper > div) {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
}

.products-area :deep(.vibrant-product-tabs .order-test-tabs) {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  min-height: 0;
}

.products-area :deep(.vibrant-product-tabs .order-test-tabs .el-tabs__header) {
  flex-shrink: 0;
}

.products-area :deep(.vibrant-product-tabs .order-test-tabs .el-tabs__content) {
  flex: 1;
  min-height: 0 !important;
  max-height: 100%;
  overflow: hidden !important;
  height: 100%;
}

.products-area :deep(.vibrant-product-tabs .order-test-tabs .el-tab-pane) {
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #c8d2de transparent;
}

.products-area :deep(.vibrant-product-tabs .order-test-tabs .el-tab-pane::-webkit-scrollbar) {
  width: 6px;
  height: 6px;
  background: transparent;
}

.products-area :deep(.vibrant-product-tabs .order-test-tabs .el-tab-pane::-webkit-scrollbar-track) {
  background: transparent;
}

.products-area :deep(.vibrant-product-tabs .order-test-tabs .el-tab-pane::-webkit-scrollbar-thumb) {
  background: #c8d2de;
  border-radius: 999px;
}

.products-area :deep(.vibrant-product-tabs .order-test-tabs .el-tab-pane::-webkit-scrollbar-thumb:hover) {
  background: #aebcca;
}

.product-card {
  background: #fff;
  border-radius: 10px;
  padding: 14px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}


@media (max-width: 1024px) {
  .two-column-layout {
    flex-direction: column;
    padding-right: 0;
  }
  .right-column {
    position: static;
    top: auto;
    right: auto;
    bottom: auto;
    width: 100%;
  }
}

/* Override VaCheckoutButtons bottom-fixed-container to position at bottom of sidebar */
.order-summary-sidebar .bottom-fixed-container {
  position: static !important;
  bottom: auto;
  left: auto;
  right: auto;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  z-index: 10;
}

.cart-sidebar .checkout-btn-wrapper {
  position: absolute !important;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
}

.order-summary-checkout-btn-wrapper :deep(.checkout-btn) {
  background: #004879 !important;
  border-color: #004879 !important;
}

.order-summary-checkout-btn-wrapper :deep(.checkout-btn.is-disabled),
.order-summary-checkout-btn-wrapper :deep(.checkout-btn:disabled) {
  background: #E0E0E0 !important;
  border-color: #E0E0E0 !important;
  color: #525252 !important;
  cursor: not-allowed;
}
</style>
